import React, { useEffect, useState, useCallback } from 'react'
import { X, Copy, Check } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { Theme } from '@/types/theme'
import type { AdminWorkspace, AdminMember, PlanRecord } from '../types/index.js'
import { getWorkspaceMembers, listPlans } from '../services/adminApi.js'

interface Props {
  workspace: AdminWorkspace | null
  onClose: () => void
  onUpdateWorkspace: (id: string, payload: Partial<{ isActive: boolean }>) => Promise<void>
  onAssignPlan: (id: string, planId: string | null) => Promise<void>
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function roleBadgeStyle(role: AdminMember['role'], theme: Theme): React.CSSProperties {
  if (role === 'owner') return { backgroundColor: `${theme.accent}22`, color: theme.accent }
  if (role === 'admin') return { backgroundColor: theme.purpleBackground, color: theme.purpleText }
  return {}
}

const MemberSkeleton: React.FC<{ border: string }> = ({ border }) => (
  <div className="flex items-center gap-3 py-3">
    <div className="w-9 h-9 rounded-full animate-pulse flex-shrink-0" style={{ backgroundColor: border }} />
    <div className="flex-1 space-y-2">
      <div className="h-3 rounded-full animate-pulse w-1/3" style={{ backgroundColor: border }} />
      <div className="h-3 rounded-full animate-pulse w-1/2" style={{ backgroundColor: border }} />
    </div>
    <div className="h-5 w-16 rounded-full animate-pulse" style={{ backgroundColor: border }} />
  </div>
)

const WorkspaceDetailPanel: React.FC<Props> = ({ workspace, onClose, onUpdateWorkspace, onAssignPlan }) => {
  const { currentTheme } = useTheme()
  const [members, setMembers] = useState<AdminMember[]>([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [plans, setPlans] = useState<PlanRecord[]>([])
  const [planLoading, setPlanLoading] = useState(false)
  const [planError, setPlanError] = useState<string | null>(null)

  const isOpen = workspace !== null

  useEffect(() => {
    if (isOpen) {
      listPlans().then(setPlans).catch(() => {})
    }
  }, [isOpen])

  const handlePlanChange = useCallback(
    async (planId: string | null) => {
      if (!workspace) return
      setPlanError(null)
      setPlanLoading(true)
      try {
        await onAssignPlan(workspace.id, planId)
      } catch (err) {
        setPlanError(err instanceof Error ? err.message : 'Failed to assign plan')
      } finally {
        setPlanLoading(false)
      }
    },
    [workspace, onAssignPlan],
  )

  const fetchMembers = useCallback(async (id: string) => {
    setMembersLoading(true)
    try {
      const result = await getWorkspaceMembers(id)
      setMembers(result)
    } catch {
      setMembers([])
    } finally {
      setMembersLoading(false)
    }
  }, [])

  useEffect(() => {
    if (workspace) {
      setMembers([])
      void fetchMembers(workspace.id)
    }
  }, [workspace, fetchMembers])

  const handleToggleActive = useCallback(async () => {
    if (!workspace) return
    setActionLoading(true)
    try {
      await onUpdateWorkspace(workspace.id, { isActive: !workspace.isActive })
    } finally {
      setActionLoading(false)
    }
  }, [workspace, onUpdateWorkspace])

  const handleCopyJoiningId = useCallback(() => {
    if (!workspace) return
    navigator.clipboard.writeText(workspace.joiningId).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }).catch(() => {})
  }, [workspace])

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 transition-opacity duration-300"
        style={{
          backgroundColor: isOpen ? 'rgba(0,0,0,0.4)' : 'transparent',
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="fixed top-0 right-0 h-full z-50 flex flex-col transition-transform duration-300 w-full sm:w-[420px]"
        style={{
          backgroundColor: currentTheme.primary,
          borderLeft: `1px solid ${currentTheme.border}`,
          boxShadow: currentTheme.boxShadow,
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {workspace && (
          <>
            {/* Header */}
            <div
              className="flex items-start justify-between px-5 py-4 border-b flex-shrink-0"
              style={{ borderColor: currentTheme.border }}
            >
              <div className="flex-1 min-w-0 pr-3">
                <h2
                  className="text-base font-semibold truncate"
                  style={{ color: currentTheme.text }}
                >
                  {workspace.name}
                </h2>
                <span
                  className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                  style={
                    workspace.isActive
                      ? { backgroundColor: currentTheme.successBackground, color: currentTheme.successText }
                      : { backgroundColor: currentTheme.errorBackground, color: currentTheme.errorText }
                  }
                >
                  {workspace.isActive ? 'Active' : 'Suspended'}
                </span>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg transition"
                style={{ color: currentTheme.secondrytext }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = currentTheme.hoverBackground
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
                }}
                aria-label="Close panel"
              >
                <X size={16} />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
              {/* Section 1: Workspace Info */}
              <div
                className="rounded-xl p-4 space-y-3"
                style={{ backgroundColor: currentTheme.secondary }}
              >
                <p
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: currentTheme.secondrytext }}
                >
                  Workspace Info
                </p>
                <div className="space-y-2 text-sm">
                  <div>
                    <span style={{ color: currentTheme.secondrytext }}>Plan</span>
                    <div className="mt-0.5 flex items-center gap-2">
                      <select
                        value={workspace.planId ?? ''}
                        disabled={planLoading}
                        onChange={(e) => handlePlanChange(e.target.value || null)}
                        className="flex-1 text-sm px-2.5 py-1.5 rounded-lg outline-none disabled:opacity-50"
                        style={{
                          backgroundColor: currentTheme.primary,
                          color: currentTheme.text,
                          border: `1px solid ${currentTheme.border}`,
                        }}
                      >
                        <option value="">None</option>
                        {plans.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <p className="mt-1 text-xs" style={{ color: currentTheme.secondrytext }}>
                      Seats: {workspace.seats ?? '—'} &middot; Monthly credits: {workspace.monthlyAiCredits?.toLocaleString() ?? '—'}
                    </p>
                    {planError && (
                      <p className="mt-1 text-xs" style={{ color: currentTheme.errorText }}>{planError}</p>
                    )}
                  </div>
                  <div>
                    <span style={{ color: currentTheme.secondrytext }}>Description</span>
                    <p className="mt-0.5 font-medium" style={{ color: currentTheme.text }}>
                      {workspace.description || 'No description'}
                    </p>
                  </div>
                  <div>
                    <span style={{ color: currentTheme.secondrytext }}>Created</span>
                    <p className="mt-0.5 font-medium" style={{ color: currentTheme.text }}>
                      {formatDate(workspace.createdAt)}
                    </p>
                  </div>
                  <div>
                    <span style={{ color: currentTheme.secondrytext }}>Joining ID</span>
                    <div className="mt-0.5 flex items-center gap-2">
                      <code
                        className="flex-1 text-xs px-2 py-1.5 rounded-lg font-mono truncate"
                        style={{
                          backgroundColor: currentTheme.primary,
                          color: currentTheme.text,
                          border: `1px solid ${currentTheme.border}`,
                        }}
                      >
                        {workspace.joiningId}
                      </code>
                      <button
                        type="button"
                        onClick={handleCopyJoiningId}
                        className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg transition"
                        style={{ color: copied ? currentTheme.successText : currentTheme.secondrytext }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.backgroundColor = currentTheme.hoverBackground
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
                        }}
                        aria-label="Copy joining ID"
                      >
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Members */}
              <div
                className="rounded-xl p-4"
                style={{ backgroundColor: currentTheme.secondary }}
              >
                <p
                  className="text-xs font-semibold uppercase tracking-wide mb-3"
                  style={{ color: currentTheme.secondrytext }}
                >
                  Members
                  {workspace.memberCount !== undefined && (
                    <span
                      className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold normal-case tracking-normal"
                      style={{
                        backgroundColor: `${currentTheme.accent}22`,
                        color: currentTheme.accent,
                      }}
                    >
                      {workspace.memberCount}
                    </span>
                  )}
                </p>

                {membersLoading ? (
                  <div className="divide-y" style={{ borderColor: currentTheme.border }}>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <MemberSkeleton key={i} border={currentTheme.border} />
                    ))}
                  </div>
                ) : members.length === 0 ? (
                  <p className="text-sm py-4 text-center" style={{ color: currentTheme.secondrytext }}>
                    No members found.
                  </p>
                ) : (
                  <div className="divide-y" style={{ borderColor: currentTheme.border }}>
                    {members.map((member) => (
                      <div key={member.membershipId} className="flex items-center gap-3 py-3">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
                          style={{
                            backgroundColor: `${currentTheme.accent}33`,
                            color: currentTheme.accent,
                          }}
                        >
                          {getInitials(member.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate" style={{ color: currentTheme.text }}>
                            {member.name}
                          </p>
                          <p className="text-xs truncate" style={{ color: currentTheme.secondrytext }}>
                            {member.email}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize"
                            style={
                              member.role === 'owner' || member.role === 'admin'
                                ? roleBadgeStyle(member.role, currentTheme)
                                : {
                                    backgroundColor: currentTheme.border,
                                    color: currentTheme.secondrytext,
                                  }
                            }
                          >
                            {member.role}
                          </span>
                          <span className="text-xs" style={{ color: currentTheme.mutedText }}>
                            {formatDate(member.joinedAt)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Actions footer */}
            <div
              className="px-5 py-4 border-t flex-shrink-0 space-y-2"
              style={{ borderColor: currentTheme.border }}
            >
              <button
                type="button"
                disabled={actionLoading}
                onClick={handleToggleActive}
                className="w-full py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                style={
                  workspace.isActive
                    ? { backgroundColor: currentTheme.errorBackground, color: currentTheme.errorText }
                    : { backgroundColor: currentTheme.successBackground, color: currentTheme.successText }
                }
              >
                {actionLoading
                  ? 'Updating...'
                  : workspace.isActive
                  ? 'Suspend Workspace'
                  : 'Activate Workspace'}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default WorkspaceDetailPanel
